import { MARGIN } from "@/lib/chartUtils";
import { ChartStuff } from "@/lib/deriveChartStuff";
import { NormalizedDataPoint } from "@/lib/PreparedDataset";
import RegressionModel from "@/types/RegressionModel";
import * as Portal from "@radix-ui/react-portal";
import * as d3 from "d3";
import { useRef, useState } from "react";
import { ChartElements } from "./charts/ChartElements";
import { Tooltip, TooltipData } from "./charts/ChartTooltip";
import { SummaryTooltips } from "./charts/SummaryTooltips";

export type CompAdjustmentResult = {
  id: string;
  address: string;
  salePrice: number;
  saleDate: Date;
  adjustmentAmount: number;
  adjustedPrice: number;
  percentChange: number;
  adjustedValue: number;
};

type ScatterplotProps = {
  width: number;
  height: number;
  data: NormalizedDataPoint[];
  subject?: { date: Date; name: string };
  hoveredData?: string;
  setHoveredData?: (val: string | undefined) => void;
  regressionType: RegressionModel | null;
  onPointClick: (point: NormalizedDataPoint) => void;
  chartStuff: ChartStuff | null;
};

export const ScatterplotSummary = ({
  width,
  height,
  data,
  subject,
  setHoveredData,
  regressionType,
  onPointClick,
  chartStuff,
}: ScatterplotProps) => {
  const boundsWidth = width - MARGIN.right - MARGIN.left;
  const boundsHeight = height - MARGIN.top - MARGIN.bottom;

  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  if (!subject) return null;
  if (!chartStuff) return null;
  const { dataXMin, dataXMax, padding } = chartStuff;

  const adjustedPrices = (chartStuff.compAdjustmentResults || []).map((r) => r.adjustedValue);
  const allPrices = [...data.map((i) => i.price), ...adjustedPrices];

  const yScale = d3
    .scaleLinear()
    .domain([d3.min(allPrices) || 0, d3.max(allPrices) || 0])
    .range([boundsHeight, 0]);

  const xScale = d3
    .scaleLinear()
    .domain([(dataXMin || 0) - padding, (dataXMax || 0) + padding])
    .range([0, boundsWidth]);

  return (
    <div className="relative" ref={containerRef}>
      <svg width={width} height={height}>
        <g
          width={boundsWidth}
          height={boundsHeight}
          transform={`translate(${[MARGIN.left, MARGIN.top].join(",")})`}
        >
          <ChartElements
            width={width}
            height={height}
            data={data}
            subject={subject}
            setHoveredData={setHoveredData}
            regressionType={regressionType}
            onPointClick={onPointClick}
            chartStuff={chartStuff}
            setTooltipData={setTooltipData}
            xScale={xScale}
            yScale={yScale}
          />

          <SummaryTooltips
            subject={subject}
            chartStuff={chartStuff}
            data={data}
            xScale={xScale}
            yScale={yScale}
            containerRef={containerRef}
          />
        </g>
      </svg>

      <Portal.Root
        container={containerRef.current}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
          marginLeft: MARGIN.left,
          marginTop: MARGIN.top,
        }}
      >
        <Tooltip tooltipData={tooltipData}>{tooltipData?.component}</Tooltip>
      </Portal.Root>
    </div>
  );
};
